import React from "react";
import { useLang } from "../i18n/LanguageContext";
import { useRevealOnScroll } from "../hooks/useRevealOnScroll";
import MagneticButton from "./MagneticButton";
import { MessageCircle, Instagram, Mail, ArrowUpRight } from "lucide-react";

const Contact = () => {
  const { t } = useLang();
  const ref = useRevealOnScroll([t]);

  const channels = [
    {
      key: "whatsapp",
      label: t.contact.whatsapp,
      value: t.contact.whatsappValue,
      icon: MessageCircle,
      href: `https://wa.me/${t.contact.whatsappValue.replace(/[^0-9]/g, "")}`,
    },
    {
      key: "instagram",
      label: t.contact.instagram,
      value: t.contact.instagramValue,
      icon: Instagram,
      href: `https://instagram.com/${t.contact.instagramValue.replace(/^@/, "")}`,
    },
    {
      key: "email",
      label: t.contact.email,
      value: t.contact.emailValue,
      icon: Mail,
      href: `mailto:${t.contact.emailValue}`,
    },
  ];

  return (
    <section id="contact" ref={ref} data-testid="contact-section" className="relative py-28 md:py-40 overflow-hidden">
      <div className="radial-blob" style={{ width: 700, height: 700, background: "#0055FF", top: "10%", left: "50%", transform: "translateX(-50%)", opacity: 0.25 }} />
      <div className="absolute inset-0 bg-grid opacity-30" aria-hidden="true" />

      <div className="relative max-w-6xl mx-auto px-6 md:px-12 text-center">
        <div className="reveal text-[11px] tracking-[0.3em] uppercase font-bold text-[#00F0FF] mb-5">
          {t.contact.overline}
        </div>
        <h2
          data-testid="contact-title"
          className="reveal font-heading font-black tracking-tighter text-4xl sm:text-5xl md:text-6xl lg:text-7xl gradient-text leading-[1.05] max-w-4xl mx-auto"
        >
          {t.contact.title}
        </h2>
        <p className="reveal mt-6 max-w-2xl mx-auto text-base sm:text-lg text-[#9BA1B0] font-body leading-relaxed">
          {t.contact.subtitle}
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {channels.map((c) => {
            const Icon = c.icon;
            return (
              <a
                key={c.key}
                data-testid={`contact-${c.key}`}
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                className="reveal group glass rounded-2xl p-7 md:p-8 text-left hover:border-[#00F0FF]/40 hover:-translate-y-1 transition-all duration-500 relative overflow-hidden"
              >
                <div
                  className="absolute -top-20 -right-20 w-48 h-48 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-3xl"
                  style={{ background: "radial-gradient(circle, rgba(0,240,255,0.3), transparent 70%)" }}
                  aria-hidden="true"
                />
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#00F0FF]/15 to-[#0055FF]/15 border border-[#00F0FF]/25">
                      <Icon size={22} className="text-[#00F0FF]" strokeWidth={1.6} />
                    </div>
                    <ArrowUpRight size={20} className="text-[#9BA1B0] group-hover:text-[#00F0FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" strokeWidth={1.6} />
                  </div>
                  <div className="mt-6 text-[10px] tracking-[0.3em] uppercase font-bold text-[#00F0FF]/70">
                    {c.label}
                  </div>
                  <div className="mt-1 font-heading font-semibold text-lg md:text-xl text-white">
                    {c.value}
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        <div className="reveal mt-14 flex items-center justify-center">
          <MagneticButton
            as="a"
            href={`mailto:${t.contact.emailValue}`}
            data-testid="contact-primary-cta"
            className="btn-primary"
          >
            {t.contact.email}
            <Mail size={18} strokeWidth={2} />
          </MagneticButton>
        </div>
      </div>
    </section>
  );
};

export default Contact;
